import { create } from "zustand";
import { persist } from "zustand/middleware";

// 4-step onboarding flow (post-rewrite — first-agent moved out of the wizard
// into the tenant dashboard, since it requires a working subdomain anyway):
//   1. Register + verify email
//   2. Sign in (auth gate — every step from here on requires Firebase JWT)
//   3. Domain branch (teammate request OR company form)
//   4. Pick subdomain → DNS provisioning → workspace ready (terminal)
//
// Granular step keys are kept so a reload can drop the user back into the exact
// sub-state they were in. `complete` is the wizard terminal — it polls the
// DNS status and shows the "workspace ready" CTA when subdomain_ready.
export type OnboardingStep =
  | "register"
  | "verify-email"
  | "login"
  | "domain-branch"
  | "teammate-requested"
  | "company-form"
  | "subdomain"
  | "complete";

// Top-level progress bar — four visible segments.
export const PROGRESS_SEGMENTS = [
  { key: "verify", label: "Verify email" },
  { key: "signin", label: "Sign in" },
  { key: "company", label: "About your business" },
  { key: "subdomain", label: "Subdomain" },
] as const;
export type ProgressSegmentKey = (typeof PROGRESS_SEGMENTS)[number]["key"];

export function segmentForStep(step: OnboardingStep): ProgressSegmentKey {
  switch (step) {
    case "register":
    case "verify-email":
      return "verify";
    case "login":
      return "signin";
    case "domain-branch":
    case "teammate-requested":
    case "company-form":
      return "company";
    case "subdomain":
    case "complete":
      return "subdomain";
  }
}

// URL-friendly slugs surfaced as ?step=<slug> on /onboarding. Lets the wizard
// state survive bookmarks, browser back/forward, and shareable links. The
// slug set is intentionally narrow: pre-auth steps (register/verify-email/
// login) live on dedicated routes, so they don't appear here.
export const STEP_SLUGS: Record<OnboardingStep, string | null> = {
  register: null,
  "verify-email": null,
  login: null,
  "domain-branch": "domain",
  "teammate-requested": "teammate",
  "company-form": "profile",
  subdomain: "subdomain",
  complete: "complete",
};

const SLUG_TO_STEP: Record<string, OnboardingStep> = {
  domain: "domain-branch",
  teammate: "teammate-requested",
  profile: "company-form",
  subdomain: "subdomain",
  complete: "complete",
};

// stepFromSlug parses a `?step=` value (case-insensitive). Returns null when
// the slug is unknown or absent — caller falls back to the persisted step.
export function stepFromSlug(slug: string | null | undefined): OnboardingStep | null {
  if (!slug) return null;
  return SLUG_TO_STEP[slug.toLowerCase()] ?? null;
}

export interface OnboardingState {
  step: OnboardingStep;
  onboardingId: string | null;
  email: string | null;
  domain: string | null;
  domainExists: boolean;
  subdomain: string | null;
  clientId: string | null;
  clientCode: string | null;
  preselectedAgent: string | null;

  setStep(s: OnboardingStep): void;
  setOnboarding(id: string, email: string): void;
  setVerified(domain: string, domainExists: boolean): void;
  setCompany(clientId: string, clientCode: string): void;
  setSubdomain(sub: string): void;
  setPreselectedAgent(code: string | null): void;
  reset(): void;
}

const initial = {
  step: "register" as OnboardingStep,
  onboardingId: null,
  email: null,
  domain: null,
  domainExists: false,
  subdomain: null,
  clientId: null,
  clientCode: null,
  preselectedAgent: null,
};

// State persists across reloads so a refresh or lost connection does not
// bounce the user back to step 1. Key kept as `vdesk:onboarding` for
// backward-compat with any stored session (stale keys are fine — missing
// fields just default to their initial values).
export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set) => ({
      ...initial,
      setStep: (step) => set({ step }),
      setOnboarding: (id, email) => set({ onboardingId: id, email }),
      setVerified: (domain, domainExists) => set({ domain, domainExists }),
      setCompany: (clientId, clientCode) => set({ clientId, clientCode }),
      setSubdomain: (sub) => set({ subdomain: sub }),
      setPreselectedAgent: (code) => set({ preselectedAgent: code }),
      reset: () => set({ ...initial }),
    }),
    { name: "vdesk:onboarding" },
  ),
);
